import { t } from '@moovly/i18n';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import useCurrentUser from '../../../hooks/useCurrentUser';
import useConfirmationModal from '../../../modals/useConfirmationModal';
import { addMembers, deleteMember } from '../../../store/members.store';
import OrganizationUserInfo from '../components/OrganizationUserInfo/OrganizationUserInfo';
import useUser from '../hooks/useUser';

interface UserOrganizationPanelProps {
  userId: string;
}

const UserOrganizationPanel: React.FC<UserOrganizationPanelProps> = ({ userId }) => {
  const { user } = useUser(userId);
  const { ConfirmationModal, openConfirmationModal } = useConfirmationModal();
  const { isPartOfOrganization, groupsWithAdminRight } = useCurrentUser();
  const dispatch = useDispatch<any>();
  const addToGroupOptions = React.useMemo<{ value: string; label: string }[]>(() => {
    return groupsWithAdminRight
      .filter((gr) => !(user?.memberships || []).some((m) => m.group_id === gr.id))
      .map((gr) => ({ value: gr.id, label: gr.name }));
  }, [user, groupsWithAdminRight]);

  const handleAddToGroup = React.useCallback(
    (group: string) => {
      dispatch(addMembers({ groupIds: [group], userId: user!.id }));
    },
    [dispatch, user]
  );

  const handleRemoveFromGroup = React.useCallback(
    (memberId: string, groupId: string) => {
      const group = groupsWithAdminRight.find((gr) => gr.id === groupId);
      openConfirmationModal(
        {
          title: t('user.organizations.remove.confirmTitle'),
          description: t('user.organizations.remove.confirmDescription', { name: group && group.name }),
        },
        () => {
          dispatch(deleteMember({ groupId, memberId }));
        }
      );
    },
    [dispatch, openConfirmationModal, groupsWithAdminRight]
  );
  if (!isPartOfOrganization) {
    return null;
  }
  return (
    <>
      <OrganizationUserInfo
        onRemoveMemberFromGroup={handleRemoveFromGroup}
        onAddToGroup={handleAddToGroup}
        memberships={user?.memberships || []}
        addToGroupOptions={addToGroupOptions}
      />
      <ConfirmationModal />
    </>
  );
};

export default UserOrganizationPanel;
